import { RequiredFeatureFlag, routes } from '@host/app/routes'
import * as Sentry from '@sentry/react'
import { useAdminStore } from '@tom-ui/admin'
import {
  useAuth,
  AuthGuard,
  authStore,
  FeatureFlagProvider,
  PATHS,
  useAppConfiguration,
  useAsyncFetch,
  userPreferencesStore,
} from '@tom-ui/utils'

import { useBooleanFlagValue, useOpenFeatureClient } from '@openfeature/react-sdk'
import { useOperationsStore } from '@tom-ui/operations'
import { usePlanningStore } from '@tom-ui/planning'
import { useStorageStore } from '@tom-ui/storage'
import { Loader } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { createRoutesFromElements, Navigate, Route, RouterProvider } from 'react-router'
import { createBrowserRouter } from 'react-router-dom'
import { loadNonCritical, PostLoginLoadError } from './post-login-load'
import { PostLoginErrorScreen } from './PostLoginErrorScreen'
import { RootElement } from './RootElement'

const mainAppSentryDns =
  'https://40ceaed00cff4203941b61553223e8db@o4505073926406144.ingest.sentry.io/4505397101658112'
const currentHost = window.location.hostname

// Shared across re-renders AND re-mounts of AppAuthWrapper so the post-login load
// runs exactly once per page load (see AppAuthWrapper). Reset naturally on reload
// — both the retry action and logout trigger a full navigation.
let postLoginLoad: Promise<void> | null = null

export const AppContent = observer(() => {
  const { environment } = useAppConfiguration()

  useEffect(() => {
    if (environment && !currentHost.includes('localhost') && !currentHost.includes('127.0.0.1')) {
      Sentry.init({
        dsn: mainAppSentryDns,
        beforeSend(event, hint) {
          const originalException = hint.originalException as any

          if (
            originalException?.name === 'AxiosError' &&
            originalException?.response?.status < 500
          ) {
            return null
          }

          return event
        },
        environment: environment,
      })
    }
  }, [environment])

  const { isLoading, isAuthenticated, logout, error, loginWithRedirect } = useAuth()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // On Safari, if you are already logged in and then open a new tab with a specific page URL, it won't automatically log you in again.
      // On Chrome, this won't happen, so it won't go into this if block.
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}`,
        },
      })
    }
  }, [isAuthenticated, isLoading, loginWithRedirect])

  useEffect(() => {
    if (error) {
      alert(`${error.message}
        If you have just signed up, we have sent you an email, please verify your email address there.
        Be aware that, the email address must end with omoqo.com, and a tenantId should be set.
      `)
      logout({ logoutParams: { returnTo: window.location.origin } })
    }
  }, [error, logout])

  if (isLoading) {
    return <Loader show />
  }

  return <AppAuthWrapper />
})

const AppAuthWrapper = observer(() => {
  const { getAccessTokenSilently, isLoading, isAuthenticated, logout } = useAuth()

  const { tenantConfigStore: operationsTenantConfigStore, equipmentStore } = useOperationsStore()
  const { tenantConfigStore: storageTenantConfigStore } = useStorageStore()
  const { tenantStore: planningTenantStore } = usePlanningStore()
  const { tenantStore, userStore } = useAdminStore()

  const { loading, error } = useAsyncFetch(async () => {
    if (isLoading || !isAuthenticated) {
      return
    }

    // Deduplicate the post-login load across re-renders AND re-mounts of
    // AppAuthWrapper. Historically an unstable useAsyncFetch dependency (or a
    // re-mount) re-ran this and hammered GetCurrentUserMetadata + DevCycle
    // sdkConfig with tens of thousands of requests, saturating the DB pool. The
    // shared module-level promise makes the load run exactly once per page load;
    // a rejected result stays cached so it keeps surfacing the error screen
    // instead of auto-retrying in a loop (the retry path is a full reload).
    const load = (postLoginLoad ??= (async () => {
      // Critical section: identity + tenant. A failure here surfaces the error
      // screen below — not an endless spinner. Previously the thrown error was
      // swallowed (only `loading` was read from useAsyncFetch), so a failing
      // step left authStore.isLoaded false forever and the app spun forever.
      await authStore.fetchAuth(getAccessTokenSilently)
      const metadata = await userStore.getUserMetadata(authStore.userId)

      if (!metadata.tenantId) {
        // e.g. a Keycloak user without the tenantId attribute: the app cannot
        // operate without a tenant, so tell the user instead of rendering a
        // broken, permission-denied shell.
        throw new PostLoginLoadError('no-tenant')
      }

      await tenantStore.loadOne(metadata.tenantId)
      const tenant = tenantStore.getTenantById(metadata.tenantId)

      authStore.setMetadata({
        userId: metadata.userId,
        tenantId: metadata.tenantId,
        permissions: metadata.permissions,
        isSuperUser: metadata.isSuperuser,
        tenantName: tenant?.name ?? '',
      })

      authStore.setAsLoaded()

      // Non-critical: per-domain config. A single 403 (e.g. an under-privileged
      // user hitting getEquipmentTypes) must NOT blank the whole app, so these
      // are settled independently and their failures are only logged.
      await loadNonCritical([
        { name: 'admin tenant configs', run: () => tenantStore.loadConfigs() },
        { name: 'operations tenant configs', run: () => operationsTenantConfigStore.loadConfigs() },
        { name: 'storage app configs', run: () => storageTenantConfigStore.loadAppConfigs() },
        { name: 'planning tenants', run: () => planningTenantStore.fetch() },
        { name: 'equipment types', run: () => equipmentStore.loadEquipmentTypes() },
        { name: 'user preferences', run: () => userPreferencesStore.loadAll() },
      ])
    })())

    await load
  }, [
    isAuthenticated,
    getAccessTokenSilently,
    isLoading,
    tenantStore,
    operationsTenantConfigStore,
    storageTenantConfigStore,
    planningTenantStore,
    equipmentStore,
    userStore,
  ])

  // Stable identity: a fresh object here re-ran FeatureFlagProvider's effect
  // (which re-initialised DevCycle) on every render.
  // tenantId is only meaningful once metadata has loaded; before that
  // authStore.metadata is still {} and authStore.tenantId is undefined at
  // runtime (its declared type lies). Guard so this render — which runs before
  // the isLoaded gate below — cannot throw on `undefined.toString()`.
  const flagsTenantId = authStore.isLoaded ? authStore.tenantId : undefined
  const featureFlagContext = useMemo(
    () => ({
      user_id: authStore.claims.sub,
      customData: { tenantId: flagsTenantId === undefined ? '' : flagsTenantId.toString() },
    }),
    [authStore.claims.sub, flagsTenantId],
  )

  if (error) {
    const kind = error instanceof PostLoginLoadError ? error.kind : 'generic'
    return (
      <PostLoginErrorScreen
        variant={kind}
        onRetry={() => window.location.reload()}
        onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })}
      />
    )
  }

  if (!authStore.isLoaded || loading) {
    return <Loader show />
  }

  return (
    <FeatureFlagProvider context={featureFlagContext}>
      <AppRoutes />
    </FeatureFlagProvider>
  )
})

const AppRoutes = observer(() => {
  const { tenantStore } = useAdminStore()
  const featureFlagClient = useOpenFeatureClient()

  const checkRequiredFeatureFlag = (requiredFeatureFlag?: RequiredFeatureFlag) => {
    return (
      !requiredFeatureFlag ||
      featureFlagClient.getBooleanValue(requiredFeatureFlag.name, false) ===
        requiredFeatureFlag.enable
    )
  }

  const vesselBookings = useBooleanFlagValue('vessel-bookings', false)
  const isNewYardEnabled = useBooleanFlagValue('new-yard', false)

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<RootElement />}>
        <Route path='/' index element={<Navigate to={PATHS.profile.url} />} />
        {routes({
          skipCraneSplit: tenantStore.skipCraneSplit,
          showUnifiedOperatorPage: tenantStore.showUnifiedOperatorPage,
          skipVesselTally: tenantStore.skipVesselTally,
          skipRailTally: tenantStore.skipRailTally,
          hasGeneralCargo: tenantStore.hasGeneralCargo,
          hasDamageCatalogue: tenantStore.hasDamageCatalogue,
          skipYardPlanning: tenantStore.skipYardPlanning,
          vesselBookings,
          isNewYardEnabled,
        })
          .filter(x => !x.hidden && checkRequiredFeatureFlag(x.requiredFeatureFlag))
          .map(route => {
            const element = route.requiredPermission ? (
              <AuthGuard
                component={route.component}
                requiredPermission={route.requiredPermission}
              />
            ) : (
              <route.component />
            )

            return <Route key={route.path} path={route.path} element={element} />
          })}
      </Route>,
    ),
  )

  return <RouterProvider router={router} />
})
